import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Volleyball, MagnifyingGlass, CheckCircle, Clock, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isPaid = (r) => (r.payment_status === 'pago' || r.payment_status === 'paid' || r.status === 'confirmada');
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function OpenPlayAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reconciling, setReconciling] = useState(false);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await axios.post(`${API}/admin/open-play/reconcile`, {}, { withCredentials: true });
      toast.success(`Verificados ${data.checked} · ${data.fixed} confirmados · ${data.reverted} revertidos (não pagos)`);
      fetchData();
    } catch { toast.error('Erro ao reconciliar'); } finally { setReconciling(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/open-play/registrations`, { withCredentials: true });
      setRows(data);
    } catch { toast.error('Erro ao carregar Open Play'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const togglePaid = async (r) => {
    const next = isPaid(r) ? 'pendente' : 'pago';
    try {
      await axios.put(`${API}/open-play/registrations/${r.id}`, { payment_status: next }, { withCredentials: true });
      toast.success(next === 'pago' ? 'Marcado como pago' : 'Marcado como pendente');
      fetchData();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(r => !q || (r.name || '').toLowerCase().includes(q) || (r.preferred_date || '').includes(q));
  const paidCount = rows.filter(isPaid).length;
  const total = rows.filter(isPaid).reduce((a, r) => a + Number(r.amount || 0), 0);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="open-play-admin-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <Volleyball size={28} className="text-[#E85D04]" weight="fill" />
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">Open Play — Inscrições</h1>
          </div>
          <button onClick={reconcile} disabled={reconciling} className="inline-flex items-center gap-2 text-sm bg-[#1A1A1A] text-white rounded-lg px-3 py-2 hover:bg-black disabled:opacity-50" data-testid="openplay-reconcile-btn">
            <ArrowsClockwise size={16} /> {reconciling ? 'Verificando...' : 'Reconciliar Stripe'}
          </button>
          <button onClick={fetchData} className="inline-flex items-center gap-2 text-sm border border-[#E6E2D8] rounded-lg px-3 py-2 hover:bg-[#FDFBF7]" data-testid="openplay-refresh">
            <ArrowsClockwise size={16} /> Atualizar
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-3" data-testid="openplay-stat-total">
            <p className="text-xs text-[#73706A] uppercase">Inscritos</p>
            <p className="text-xl font-['Outfit'] font-bold text-[#1A1A1A]">{rows.length}</p>
          </div>
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-3" data-testid="openplay-stat-paid">
            <p className="text-xs text-[#73706A] uppercase">Pagos</p>
            <p className="text-xl font-['Outfit'] font-bold text-[#2D6A4F]">{paidCount}</p>
          </div>
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-3" data-testid="openplay-stat-revenue">
            <p className="text-xs text-[#73706A] uppercase">Arrecadado</p>
            <p className="text-xl font-['Outfit'] font-bold text-[#E85D04]">{money(total)}</p>
          </div>
        </div>

        <div className="relative mb-4">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome ou data..." className="pl-10" data-testid="openplay-search" />
        </div>

        <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
          {loading ? (
            <p className="p-6 text-center text-[#73706A]">Carregando...</p>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-center text-[#73706A]" data-testid="openplay-empty">Nenhuma inscrição de Open Play.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FDFBF7] text-[#73706A] text-xs uppercase">
                  <th className="text-left px-4 py-2">Nome</th>
                  <th className="text-left px-4 py-2">Dia do Open</th>
                  <th className="text-right px-4 py-2 hidden sm:table-cell">Valor</th>
                  <th className="text-center px-4 py-2">Pagamento</th>
                  <th className="text-center px-4 py-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-[#F0EDE5]" data-testid={`openplay-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium text-[#1A1A1A]">
                      {r.name}
                      {r.is_student ? <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Aluno</span> : <span className="ml-2 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Visitante</span>}
                      <div className="text-[11px] text-[#73706A] mt-0.5" data-testid={`openplay-method-${i}`}>
                        {r.payment_method || '—'}
                        {r.zelle_receipt_path
                          ? <a href={`${API}/open-play/${r.id}/receipt?token=${localStorage.getItem('access_token') || ''}`} target="_blank" rel="noreferrer" className="text-[#0077B6] underline ml-1" data-testid={`openplay-receipt-${i}`}>ver comprovante</a>
                          : (r.payment_method === 'Zelle' ? <span className="text-[#9B2226] font-semibold"> · SEM comprovante</span> : '')}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[#1A1A1A]">{r.preferred_date || '-'}</td>
                    <td className="px-4 py-2.5 text-right text-[#73706A] hidden sm:table-cell">{money(r.amount)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {isPaid(r) ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full" data-testid={`openplay-paid-${i}`}><CheckCircle size={13} weight="fill" /> Pago</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full" data-testid={`openplay-pending-${i}`}><Clock size={13} weight="fill" /> Pendente</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button onClick={() => togglePaid(r)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${isPaid(r) ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`} data-testid={`openplay-toggle-${i}`}>
                        {isPaid(r) ? 'Marcar Pendente' : 'Marcar Pago'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
